import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { usePersistedOpen } from '@/hooks/use-persisted-open';
import { cn } from '@/utils/cn';

/**
 * A page-level metrics block that thins out when collapsed — never disappears.
 *
 * Collapsed: a bordered strip with the title and a one-line summary of the key
 * figures, so people still see that an overview is there. Expanded: the full
 * tiles and charts.
 *
 * List pages share one open/closed preference. The dashboard must pass its own
 * `storageKey` — collapsing Overview on Giving/People must not empty home.
 */

/** Shared preference for list-page Overview panels. */
const INSIGHTS_OPEN_STORAGE_KEY = 'trinity.insights.open';

/** Dashboard home — independent of the list-page preference. */
export const DASHBOARD_INSIGHTS_OPEN_STORAGE_KEY = 'trinity.insights.dashboard.open';

export interface InsightsSummaryItem {
  label: string;
  value: ReactNode;
  href?: string;
}

export interface CollapsibleInsightsProps {
  /** Header label. Defaults to the shared "Overview" string. */
  title?: string;
  /**
   * Compact figures shown in the thin strip when collapsed.
   * Pass the same headline numbers the expanded tiles lead with.
   */
  summary?: InsightsSummaryItem[];
  /** Controls rendered on the right of the strip, before show/hide. */
  headerTrailing?: ReactNode;
  /** Whether the section starts open when no preference is stored. */
  defaultOpen?: boolean;
  /**
   * localStorage key for open state. Defaults to the shared list-page key.
   * Pass a distinct key when Overview is the page’s main content (dashboard).
   */
  storageKey?: string;
  children: ReactNode;
  className?: string;
}

export const InsightsSummary = ({ items }: { items: InsightsSummaryItem[] }) => {
  if (items.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex min-w-0 max-w-full items-baseline gap-1.5 text-caption"
        >
          <span className="shrink-0 text-fg-secondary">{item.label}</span>
          {item.href ? (
            <Link
              to={item.href}
              className="min-w-0 truncate font-medium tabular-nums text-brand hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {item.value}
            </Link>
          ) : (
            <span className="min-w-0 truncate font-medium tabular-nums text-fg">{item.value}</span>
          )}
        </div>
      ))}
    </div>
  );
};

export const CollapsibleInsights = ({
  title,
  summary,
  headerTrailing,
  defaultOpen = true,
  storageKey = INSIGHTS_OPEN_STORAGE_KEY,
  children,
  className,
}: CollapsibleInsightsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = usePersistedOpen(storageKey, defaultOpen);
  const label = title ?? t('insights.title');
  const summaryItems = summary?.filter((item) => item.value !== undefined && item.value !== null) ?? [];
  const toggle = () => setOpen(!open);

  return (
    <section
      className={cn(
        'mb-6 overflow-hidden rounded-lg border border-border bg-surface',
        className,
      )}
    >
      <div className="flex items-center gap-2 px-4 py-2.5">
        <button
          type="button"
          className="flex min-h-0 min-w-0 flex-1 items-center gap-3 rounded-md text-left hover:bg-surface-sunken/60"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className="shrink-0 text-body font-medium text-fg">{label}</span>

          {!open && summaryItems.length > 0 ? (
            <div className="min-w-0 flex-1">
              <InsightsSummary items={summaryItems} />
            </div>
          ) : (
            <span className="min-w-0 flex-1" />
          )}
        </button>

        {headerTrailing ? (
          <div
            className="shrink-0"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {headerTrailing}
          </div>
        ) : null}

        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-1 text-caption text-fg-secondary hover:bg-surface-sunken/60"
          aria-expanded={open}
          onClick={toggle}
        >
          {open ? t('insights.hide') : t('insights.show')}
          <ChevronDown
            aria-hidden
            className={cn('size-4 transition-transform', open ? 'rotate-180' : 'rotate-0')}
          />
        </button>
      </div>

      {open ? (
        <div className="space-y-4 border-t border-border px-4 py-4">{children}</div>
      ) : null}
    </section>
  );
};
